import { getGPUTier, type TierResult } from "detect-gpu";

// Splat capacity is chosen by form factor only: desktop or mobile. Grading it
// by GPU tier meant the same project rendered a different number of splats on
// two desktops, so QA could not compare bug reports against anything. Two
// values keep it reproducible while respecting that phones and tablets share
// VRAM with system memory.
//
// Cost per splat is 64 B in the data texture plus 4 B in the index buffer:
//   desktop 7M → 476 MB     mobile 3M → 204 MB
// Texture geometry stays legal either way (7M is 4096 x 6836 texels against a
// 16384 limit).
//
// Desktop was raised 5M → 7M so heavy scenes lose less detail: at 5M, mayu
// (13.5M splats in the .gsx) showed only 37% of the scene; 7M lifts that to
// ~51% and closes most of the visible gap to a full-detail render. The cost is
// frame rate on the heaviest scenes — per-frame work scales with resident
// splats — so this is a detail-for-fps trade, taken deliberately for desktop
// where VRAM and GPU headroom allow it.
export const DESKTOP_CAPACITY = 7_000_000;
export const MOBILE_CAPACITY = 3_000_000;

export interface GpuTierInfo {
  capacitySplats: number;
  isMobile: boolean;
  result: TierResult | null; // null when detection threw — see `error`
  error?: unknown;
}

/**
 * detect-gpu knows the form factor, but it can throw (blocked storage, no
 * WebGL) and it is not worth failing over — fall back to the user agent.
 * Safari on iPad reports a desktop UA, so touch points disambiguate it.
 */
function isMobileUserAgent(): boolean {
  // Any positive signal wins. A negative `userAgentData.mobile` is not proof of
  // desktop: WebViews and "request desktop site" leave it false while the UA
  // string still says phone, and iPad Safari reports a Macintosh UA outright —
  // touch points are what give it away.
  const uaData = (navigator as unknown as { userAgentData?: { mobile?: boolean } }).userAgentData;
  if (uaData?.mobile === true) return true;
  const ua = navigator.userAgent;
  if (/Android|iPhone|iPod|iPad|Mobile|Tablet|Silk/i.test(ua)) return true;
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

export async function detectGpuCapacity(
  gl?: WebGL2RenderingContext,
): Promise<GpuTierInfo> {
  try {
    const result = await getGPUTier({ glContext: gl });
    const isMobile = result.isMobile ?? isMobileUserAgent();
    return { capacitySplats: isMobile ? MOBILE_CAPACITY : DESKTOP_CAPACITY, isMobile, result };
  } catch (error) {
    const isMobile = isMobileUserAgent();
    return { capacitySplats: isMobile ? MOBILE_CAPACITY : DESKTOP_CAPACITY, isMobile, result: null, error };
  }
}
